import * as React from 'react';
import { observer } from 'mobx-react';

import { copyToClipboard } from 'shared/entities/common/utils';
import { useAppNotificationsStore } from 'stores/index';
import { AppNotificationType } from 'shared/entities/appNotifications';
import { Input } from 'shared/newComponents/form';
import { Button } from 'shared/newComponents/buttons';
import { ComponentType } from 'shared/entities/components/Component';
import { ButtonSize } from 'shared/newEntities/components/Button';
import { useChannelsModalStore } from 'stores/ChannelsModalStore';
import { FieldModel } from 'shared/models/form';
import { LoadingStage } from 'shared/entities/meta';
import FadeScroll from 'shared/newComponents/FadeScroll';
import { CommonDirection } from 'shared/entities/common/client';

import './FormWithToken.modules.scss';
import DefaultInstructionSlot from './DefaultInstructionSlot';

export type InstructionSlotProps = {
  url?: string;
  creatingBotName?: string;
  command?: string;
  onCopyCommand?: () => void;
};

type Props = {
  token: FieldModel<string>;
  onCreate: () => Promise<BaseResponse<{ channelId: string }>>;
  placeholder: string;
  url?: string;
  command?: string;
  creatingBotName?: string;
  creatingStage?: LoadingStage;
  disabled?: boolean;
  InstructionSlot?: React.ComponentType<InstructionSlotProps>;
};

const FormWithToken: React.FC<Props> = ({
  token,
  onCreate,
  placeholder,
  url,
  command,
  creatingBotName,
  creatingStage,
  disabled,
  InstructionSlot
}: Props) => {
  const appNotificationsStore = useAppNotificationsStore();
  const { store: modalStore } = useChannelsModalStore();

  const handleCopyToClipboard = React.useCallback(async () => {
    if (!command) {
      return;
    }

    const response = await copyToClipboard(command);

    if (!response.isError) {
      appNotificationsStore.open({
        type: AppNotificationType.success,
        title: (t) =>
          t('ChannelsModal.ChannelForm.FormWithToken.copy', {
            ns: 'newContainers'
          })
      });
    }
  }, []);

  const handleCreate = React.useCallback(async () => {
    const response = await onCreate();
    if (!response.isError) {
      modalStore.handleChannelCreate(response.data.channelId);
    }
  }, []);

  const Slot = InstructionSlot ?? DefaultInstructionSlot;

  return (
    <FadeScroll
      boxShadowCompensation={CommonDirection.left}
      styleName="form__scroll-wrapper"
    >
      <Slot
        url={url}
        creatingBotName={creatingBotName}
        command={command}
        onCopyCommand={handleCopyToClipboard}
      />

      <Input
        styleName="form__input"
        label={(t) =>
          t('ChannelsModal.ChannelForm.token', {
            ns: 'newContainers'
          })
        }
        value={token.value}
        onChange={token.changeValue}
        error={token.error}
        onBlur={token.validate}
        placeholder={placeholder}
      />
      <Button
        styleName="form__button"
        size={ButtonSize.lg}
        type={ComponentType.button}
        onClick={handleCreate}
        isLoading={creatingStage === LoadingStage.LOADING}
        disabled={disabled}
        fullWidth
      >
        {(t) =>
          t('ChannelsModal.ChannelForm.button', {
            ns: 'newContainers'
          })
        }
      </Button>
    </FadeScroll>
  );
};

export default observer(FormWithToken);
